import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Searchspan.css";

const SearchSpanTest = () => {
  const navigate = useNavigate();

  const [service, setService] = useState("");
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================
  // SCROLL TO TOP
  // ==========================================

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ==========================================
  // ACCESS CHECK + SERVICE
  // ==========================================

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      const email = localStorage.getItem("user_email");
      const authed = localStorage.getItem("rg_auth") === "1";

      if (!authed || !email) {
        navigate("/dashboard-login");
        return;
      }

      try {
        const res = await axios.get("/check-access", {
          params: {
            email,
            test: "search_span",
          },
        });

        console.log("Search Span API:", res.data);

        if (!res.data.allowed) {
          if (isMounted) {
            setHasAccess(false);
          }
          return;
        }

        const userService =
          res.data.service ||
          res.data.metadata?.service ||
          res.data.user?.metadata?.service ||
          localStorage.getItem("service") ||
          "";

        if (isMounted) {
          setService(userService);
          setHasAccess(true);
        }
      } catch (err) {
        console.error("Search Span access check failed:", err);

        if (isMounted) {
          setHasAccess(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ==========================================
  // SERVICE IDENTIFICATION
  // ==========================================

  const normalizedService = service.trim().toLowerCase();

  const isVisionIgnite =
    normalizedService === "forge your peak - vision ignite";

  const isIgniteOrAscend =
    normalizedService === "forge your peak - ignite" ||
    normalizedService === "forge your peak - ascend";

  // ==========================================
  // RETURN HOME
  // ==========================================

  const returnHome = () => {
    if (isVisionIgnite) {
      navigate("/measure");
    } else if (isIgniteOrAscend) {
      navigate("/ignite/dashboard");
    } else {
      navigate("/");
    }
  };

  // ==========================================
  // START TEST
  // ==========================================

  const startTest = () => {
    navigate("/search-span/play");
  };

  // ==========================================
  // LOADING
  // ==========================================

  if (loading || hasAccess === null) {
    return (
      <div className="rg-login-wrapper">
        <p style={{ textAlign: "center" }}>
          Checking access...
        </p>
      </div>
    );
  }

  // ==========================================
  // ACCESS DENIED
  // ==========================================

  if (hasAccess === false) {
    return (
      <div className="access-denied-container">
        <h2>🚫 Access Denied</h2>

        <p>You don't have access to this test.</p>

        <button
          type="button"
          onClick={() => navigate("/")}
        >
          Go Back
        </button>
      </div>
    );
  }

  // ==========================================
  // SERVICE-BASED CONTENT
  // Ignite + Ascend = Original content
  // Vision Ignite = Separate content
  // ==========================================

  const pageTitle = isVisionIgnite
    ? "Search Span Assessment"
    : "Search Span Test";

  const pageDescription = isVisionIgnite
    ? "Search span measures how efficiently you scan a visual field, identify targets, and distinguish relevant information from distractions. This assessment focuses on visual scanning, attention, and target detection."
    : 'Search span measures how quickly and accurately you can scan a visual field and locate the correct target among multiple distractions. It trains the brain to "Scan → Identify → Respond."';

  return (
    <div className="rg-login-wrapper">

      {/* ==========================================
          RETURN HOME BUTTON
      ========================================== */}

      <div className="return-home-wrapper">
        <button
          type="button"
          className="return-home-btn"
          onClick={returnHome}
        >
          ← Return Home
        </button>
      </div>

      {/* ==========================================
          HEADER SECTION
      ========================================== */}

      <div className="reaction-header-card">

        <h2 className="reaction-title">
          {pageTitle}
        </h2>

        <p className="reaction-subtitle">
          {pageDescription}
        </p>

        <div className="reaction-pill">
          Test type:{" "}
          <strong>
            Visual scanning → Target detection
          </strong>
        </div>

        <hr className="reaction-divider" />

        {/* ==========================================
            WHY SECTION
        ========================================== */}

        <div className="reaction-why-card">

          <h3 className="why-title">
            {isVisionIgnite
              ? "Why Search Span Matters"
              : "Why search span matters in sport"}
          </h3>

          {isVisionIgnite ? (
            <>
              {/* VISION IGNITE CONTENT */}

              <p className="why-description">
                Efficient visual scanning helps you identify
                relevant targets while ignoring distractions.
                Search span focuses on how you scan a visual
                field, maintain attention, and locate information.
              </p>

              <div className="why-tags">
                <span>Visual Scanning</span>
                <span>Target Detection</span>
                <span>Selective Attention</span>
                <span>Visual Search</span>
                <span>Focus</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Visual scanning:</strong>{" "}
                  Scan the visual field to locate specific targets.
                </li>

                <li>
                  <strong>Target detection:</strong>{" "}
                  Identify the correct target among other items.
                </li>

                <li>
                  <strong>Selective attention:</strong>{" "}
                  Focus on the required information while
                  ignoring distractions.
                </li>

                <li>
                  <strong>Response accuracy:</strong>{" "}
                  Track how accurately you identify targets
                  within the available time.
                </li>
              </ul>
            </>
          ) : (
            <>
              {/* ORIGINAL IGNITE + ASCEND CONTENT */}

              <p className="why-description">
                In sports, athletes constantly scan their
                environment to track teammates, opponents,
                and ball movement. A strong search span helps
                players process visual information faster and
                make better tactical decisions.
              </p>

              <div className="why-tags">
                <span>Faster visual scanning</span>
                <span>Better situational awareness</span>
                <span>Improved focus</span>
                <span>Better field awareness</span>
                <span>Quick target recognition</span>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ==========================================
          HOW GAME WORKS
      ========================================== */}

      <div className="how-card">

        <h2 className="how-title">
          How the game works
        </h2>

        <ul className="how-list">

          <li>
            A grid of numbers from{" "}
            <strong>1 to 50</strong> will be displayed randomly.
          </li>

          <li>
            Your task is to{" "}
            <strong>
              find and click the numbers in order (1 → 2 → 3...)
            </strong>.
          </li>

          <li>
            You will have <strong>60 seconds</strong> to find
            as many numbers as possible.
          </li>

          <li>
            Each correct number increases your score.
          </li>

          <li>
            Your final score is based on{" "}
            <strong>
              how many numbers you correctly find within time
            </strong>.
          </li>

        </ul>

        <p className="how-note">
          Best practice: use the same device and posture
          when comparing results.
        </p>

      </div>

      {/* ==========================================
          START TEST
      ========================================== */}

      <div className="start-test-section">

        <h2>
          {isVisionIgnite
            ? "Ready to Assess Your Visual Scanning?"
            : "Ready to test your visual scanning?"}
        </h2>

        <button
          type="button"
          className="start-test-btn"
          onClick={startTest}
        >
          Start Test
        </button>

      </div>

    </div>
  );
};

export default SearchSpanTest;